import * as React from 'react';
import './styles.scss';
import {
  MaterialIcon,
  MaterialIconName,
  Btn,
  ButtonTypes,
} from 'client/shared/components/base';
import { ContentDrawer } from 'client/shared/components/content-drawer';
import { Case, unionOfEnum } from 'core';
import { useMutationInfo } from 'client/shared/containers/mutation';
import { MutationInfos } from 'client/shared/graphql-mutations';
import { RESPONDENT_STATE_IN_LOCAL_STORAGE } from 'client/respondent/core/reducers/context';
import { ClientUrlUtils } from 'client/shared/core/helpers';
import { useLocalStorage } from 'client/shared/helpers/hooks';
import { CurrentUser } from 'client/respondent/hooks';
import logo from 'client/assets/polco-logo-dark-text.svg';
import safety from './assets/safety.svg';
import {
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useRedirect } from 'client/shared/hooks';
const baseClass = 'pn-respondent-sidebar';

export enum SidebarType {
  TRANSIENT = 'TRANSIENT',
  PERMANENT = 'PERMANENT',
}

interface SideBarPropsBase {
  readonly respondent: CurrentUser | null;
  readonly isGuest: boolean;
  readonly hideWhyCreateAccountSection?: boolean;
  readonly hideZipCodeSection?: boolean;
  readonly hideAuthButtons?: boolean;
}

export type SideBarProps =
  | Case<SidebarType.PERMANENT, SideBarPropsBase>
  | Case<
      SidebarType.TRANSIENT,
      {
        readonly open: boolean;
        readonly close: () => void;
      } & SideBarPropsBase
    >;

export const SideBarProps = unionOfEnum(SidebarType, {
  ...SidebarType,
}).andType<SideBarProps>();

export const pagesCopy = {
  guest: {
    guest: 'You are in guest mode!',
    feed: 'Guest Feed',
    accountSetup: 'Account Setup',
    login: 'Login',
  },
  loggedIn: {
    hello: 'Hello',
    links: {
      aboveDivider: [
        {
          text: 'Home',
          icon: MaterialIconName.HOME,
          path: ClientUrlUtils.respondent.feed.path(),
        },
        {
          text: 'Feeds',
          icon: MaterialIconName.ARTICLE,
          path: ClientUrlUtils.respondent.subscriptions.path(),
        },
      ],
      belowDivider: {
        account: {
          text: 'Account',
          icon: MaterialIconName.ACCOUNT_CIRCLE,
          path: ClientUrlUtils.respondent.account.path(),
        },
        logOut: { text: 'Log Out', icon: MaterialIconName.SENSOR_DOOR },
      },
    },
  },
  notLoggedIn: {
    welcome: 'Welcome to Polco',
    alreadyHaveAccount: 'Already have an account?',
    login: 'Login',
    createAccount: 'Create Account',
    paragraphs: [
      {
        iconType: 'material',
        icon: MaterialIconName.THUMBS_UP_DOWN,
        outline: true,
        text: 'Your communities need your input on important issues.',
      },
      {
        iconType: 'material',
        icon: MaterialIconName.NOT_LISTED_LOCATION,
        outline: true,
        text: 'Get access to local area insights and decision making data.',
      },
      {
        iconType: 'material',
        icon: MaterialIconName.FAVORITE_BORDER,
        text: "It's free and completely anonymous.",
      },
      {
        iconType: 'svg',
        icon: safety,
        text: 'Your information is completely private and will never be sold.',
      },
    ],
  },
};

// const loggedInpages: readonly PageStructure[] = [
//   {
//     title: pagesCopy.loggedIn.feed,
//     to: ClientUrlUtils.respondent.feed.path(),
//     ariaLabel: 'Polco resident feed',
//   },
//   {
//     title: pagesCopy.loggedIn.subscriptions,
//     to: '/res/subscriptions',
//   },
//   {
//     title: pagesCopy.loggedIn.account,
//     to: '/res/account',
//   },
// ];

export const SideBarContentsUnauthenticated: React.FC<SideBarProps> = (p) => {
  const redirect = useRedirect();
  return (
    <div
      className={`${baseClass} ${
        p.type === SidebarType.TRANSIENT ? 'mod-no-padding' : ''
      }`}
    >
      <div className="d-flex flex-column">
        <Typography variant="h4">{pagesCopy.notLoggedIn.welcome}</Typography>
        {!p.hideAuthButtons && (
          <>
            <Divider />
            <List style={{ width: '100%' }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    redirect(ClientUrlUtils.respondent.feed.path(), { push: true });
                    p.type === SidebarType.TRANSIENT && p.close();
                  }}
                >
                  <ListItemIcon>
                    <MaterialIcon
                      icon={MaterialIconName.HOME}
                      iconType="material-symbols-outlined"
                    />
                  </ListItemIcon>
                  <ListItemText primary="Home" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    redirect(ClientUrlUtils.respondent.signup.path(), {
                      push: false,
                    });
                    p.type === SidebarType.TRANSIENT && p.close();
                  }}
                >
                  <ListItemIcon>
                    <MaterialIcon
                      icon={MaterialIconName.ARTICLE_PERSON}
                      iconType="material-symbols-outlined"
                    />
                  </ListItemIcon>
                  <ListItemText primary="Sign up" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    redirect(ClientUrlUtils.respondent.login.path(), {
                      push: false,
                    });
                    p.type === SidebarType.TRANSIENT && p.close();
                  }}
                >
                  <ListItemIcon>
                    <MaterialIcon
                      icon={MaterialIconName.ACCOUNT_CIRCLE}
                      iconType="material-symbols-outlined"
                    />
                  </ListItemIcon>
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
      </div>
    </div>
  );
};

export const SidebarContentsAuthenticated: React.FC<SideBarProps> = (props) => {
  const localStorage = useLocalStorage();
  const { fn: logout } = useMutationInfo(MutationInfos.logout);
  const respondentName = props.respondent?.user?.respondent?.firstName;
  const redirect = useRedirect();

  return (
    <div
      className={`${baseClass} ${
        props.type === SidebarType.TRANSIENT ? 'mod-no-padding' : ''
      }`}
    >
      <Stack>
        <Typography variant="h4">Hello, {respondentName}</Typography>
        <Divider />
        <List style={{ width: '100%' }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                redirect(ClientUrlUtils.respondent.feed.path(), { push: true });
                props.type === SidebarType.TRANSIENT && props.close();
              }}
            >
              <ListItemIcon>
                <MaterialIcon
                  icon={MaterialIconName.HOME}
                  iconType="material-symbols-outlined"
                />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                redirect(ClientUrlUtils.respondent.account.path(), { push: true });
                props.type === SidebarType.TRANSIENT && props.close();
              }}
            >
              <ListItemIcon>
                <MaterialIcon
                  icon={MaterialIconName.ACCOUNT_CIRCLE}
                  iconType="material-symbols-outlined"
                />
              </ListItemIcon>
              <ListItemText primary={'Account'} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                redirect(ClientUrlUtils.respondent.subscriptions.path(), {
                  push: true,
                });
                props.type === SidebarType.TRANSIENT && props.close();
              }}
            >
              <ListItemIcon>
                <MaterialIcon
                  icon={MaterialIconName.ARTICLE_PERSON}
                  iconType="material-symbols-outlined"
                />
              </ListItemIcon>
              <ListItemText primary="Following" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={async () => {
                await logout();
                if (localStorage) {
                  localStorage.removeItem(RESPONDENT_STATE_IN_LOCAL_STORAGE);
                }
              }}
            >
              <ListItemIcon>
                <MaterialIcon
                  icon={MaterialIconName.EXIT_TO_APP}
                  iconType="material-symbols-outlined"
                />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Stack>
    </div>
  );
};

export const Sidebar: React.FC<SideBarProps> = (p) => {
  const loggedIn = !!p.respondent?.user?.respondent?.id;

  const sidebarContents = () => {
    if (!loggedIn || p.isGuest) {
      return <SideBarContentsUnauthenticated {...p} />;
    } else return <SidebarContentsAuthenticated {...p} />;
  };

  return p.type === SidebarType.TRANSIENT ? (
    <ContentDrawer clickedOutside={p.close} open={p.open} side="left">
      <div className="d-flex flex-column">
        <div className="d-flex flex-row align-items-center mb-3 p-3">
          <img alt="Polco's Logo" className={`${baseClass}-img ml-3`} src={logo} />
          <div className={`${baseClass}-spacer flex-grow-1`}></div>
          <Btn
            action={p.close}
            ariaControls="content-drawer-menu"
            className="p-0"
            size="small"
            type={ButtonTypes.SEAMLESS}
          >
            <MaterialIcon icon={MaterialIconName.CLOSE} />
          </Btn>
        </div>
        {sidebarContents()}
      </div>
    </ContentDrawer>
  ) : (
    <div className={`${baseClass}-permanent d-flex flex-column h-100`}>
      <h2 className="d-none">Menu</h2>
      {sidebarContents()}
    </div>
  );
};
Sidebar.displayName = 'Sidebar';
